import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import FFLinkButton from './buttons/FFLinkButton';
import { getImageUrl, useOnLoad } from '../utils/cloudinary';
import ChefCollabTag from './tags/ChefCollabTag';
import InventoryTags from './tags/InventoryTags';
import Price from './pages/menu/Price';
import BummerButton from './pages/menu/BummerButton';
import useIsTouchDevice from '../utils/useIsTouchDevice';
import styles from './ProductCard.module.css';

/**
 * @param {object} props
 * @param {string=} props.className
 * @param {FF.AWSProductCloudinaryMediaImageType} props.imageType
 * @param {FF.AWSProduct} props.product
 * @param {number} props.imageWidth
 * @param {boolean=} props.eagerLoad
 * @param {string=} props.sizes
 * @param {boolean=} props.isHidden
 */
function ProductCardImage({
  className = '',
  product,
  imageType,
  imageWidth,
  eagerLoad = false,
  sizes,
  isHidden,
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  const onLoadFn = () => setIsLoaded(true);

  const ref = useOnLoad(onLoadFn);
  /** @type {React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>} */
  const sharedProps = {
    height:
      imageWidth *
      (product.cloudinaryMedia[imageType].height /
        product.cloudinaryMedia[imageType].width),
    width: imageWidth,
    loading: eagerLoad ? 'eager' : 'lazy',
  };

  /**
   * @param {string} imageExtension
   */
  const getImagePlaceholderSrcSet = imageExtension =>
    getImageUrl(
      imageExtension,
      imageType,
      false,
      product.sku,
      120,
      product.cloudinaryMedia[imageType].version,
    );

  /**
   * @param {string} imageExtension
   */
  const getProductImageSrcSet = imageExtension =>
    `${getImageUrl(
      imageExtension,
      imageType,
      false,
      product.sku,
      imageWidth,
      product.cloudinaryMedia[imageType].version,
    )} ${imageWidth}w, ${getImageUrl(
      imageExtension,
      imageType,
      false,
      product.sku,
      imageWidth * 1.5,
      product.cloudinaryMedia[imageType].version,
    )} ${imageWidth * 1.5}w, ${getImageUrl(
      imageExtension,
      imageType,
      false,
      product.sku,
      imageWidth * 2,
      product.cloudinaryMedia[imageType].version,
    )} ${imageWidth * 2}w`;

  return (
    <div className={className}>
      <picture>
        <source srcSet={getImagePlaceholderSrcSet('avif')} type="image/avif" />
        <source srcSet={getImagePlaceholderSrcSet('webp')} type="image/webp" />
        <img
          alt=""
          role="presentation"
          className={styles.productImagePlaceholder}
          {...sharedProps}
          src={getImageUrl(
            'png',
            imageType,
            false,
            product.sku,
            120,
            product.cloudinaryMedia[imageType].version,
          )}
        />
      </picture>
      <picture>
        <source
          data-testid={`ProductCard-source-${imageType}`}
          srcSet={getProductImageSrcSet('avif')}
          type="image/avif"
        />
        <source srcSet={getProductImageSrcSet('webp')} type="image/webp" />
        <img
          alt={product.cloudinaryMedia[imageType].context?.custom.alt}
          aria-hidden={isHidden}
          className={`${styles.productImage} ${isLoaded ? styles.loaded : ''}`}
          onLoad={() => {
            onLoadFn();
          }}
          ref={ref}
          sizes={sizes}
          src={`${getImageUrl(
            'png',
            imageType,
            false,
            product.sku,
            imageWidth * 2,
            product.cloudinaryMedia[imageType].version,
          )}`}
          {...sharedProps}
        />
      </picture>
    </div>
  );
}

/**
 * @param {object} props
 * @param {string=} props.className
 * @param {string=} props.fridgeId
 * @param {number=} props.imageWidth
 * @param {FF.AWSProduct} props.product
 * @param {string=} props.sizes
 * @param {boolean=} props.withMoreInfoButton
 * @param {boolean=} props.eagerLoad
 */
export default function ProductCard({
  className = '',
  fridgeId,
  imageWidth = 480,
  product,
  sizes,
  withMoreInfoButton = false,
  eagerLoad = false,
}) {
  const [isHover, setIsHover] = useState(false);
  const onFocusProps = {
    onFocus: () => setIsHover(true),
    onBlur: () => setIsHover(false),
  };

  /**
   * uses UA parsing in `isTouchDevice` to determine whether or not to apply `onHoverProps`
   * which will show a second image on hover.
   * touch devices are showing some strange behavior with the product cards,
   * where a tap is only registering a "mouseenter" event and not "click"
   *
   * TODO: since UA parsing can be unreliable, find some other way to resolve this
   * something related to React and/or Gatsby's <Link> component is the cause
   * */
  const isTouchDevice = useIsTouchDevice();
  const onHoverProps = {
    onMouseEnter: () => setIsHover(true),
    onMouseLeave: () => setIsHover(false),
  };
  const [useHoverProps, setUseHoverProps] = useState(false);
  useEffect(() => {
    setUseHoverProps(!isTouchDevice);
  }, [isTouchDevice]);

  const productUrl = `${product.productPagePath}${
    fridgeId ? `?fridgeId=${fridgeId}` : ''
  }`;

  const figureComponent = (
    <figure
      className={`${styles.wrapper} ${product.price ? styles.withPrice : ''} ${
        product.count === 0 ? styles.unavailable : ''
      } ${isHover ? styles.hover : ''} ${withMoreInfoButton ? className : ''}`}
      {...(useHoverProps ? onHoverProps : {})}
    >
      {product.isChefCollab && (
        <ChefCollabTag className={styles.chefCollabTag} />
      )}

      {product.hasTopDownImage && (
        <ProductCardImage
          className={styles.topDownImageWrapper}
          eagerLoad={eagerLoad}
          imageType="TopDown"
          imageWidth={imageWidth}
          product={product}
          sizes={sizes}
          isHidden={isHover}
        />
      )}
      <ProductCardImage
        className={styles.blockOutImageWrapper}
        eagerLoad={eagerLoad && !product.hasTopDownImage}
        imageType="BlockOut"
        imageWidth={imageWidth}
        product={product}
        sizes={sizes}
        isHidden={product.hasTopDownImage && !isHover}
      />

      <figcaption className={styles.details}>
        <h3 className={styles.productName}>{product.prettyName}</h3>
        {product.formattedCaloriesStrings.map(calStr => (
          <p className={styles.calories} key={calStr}>
            {calStr}
          </p>
        ))}
        <InventoryTags className={styles.productTag} product={product} />
        {product.price && <Price className={styles.price} product={product} />}
      </figcaption>
      {/*

      removing bummer button for now, until tracking is implemented

      {product.count === 0 && (
        <BummerButton
          className={styles.bummerButton}
          productSKU={product.sku}
        />
      )} */}
      {withMoreInfoButton && (
        <FFLinkButton
          className={styles.learnMoreButton}
          buttonType="secondary"
          href={productUrl}
          {...onFocusProps}
        >
          More Info
        </FFLinkButton>
      )}
    </figure>
  );

  if (withMoreInfoButton) {
    return figureComponent;
  }

  return (
    <Link
      className={`${styles.linkWrapper} ${className}`}
      to={productUrl}
      {...onFocusProps}
    >
      {figureComponent}
    </Link>
  );
}
